import type {
  AIContentClassification,
  AIProvenanceFlags,
  AIProvenanceRecord,
  ProvenanceHealth,
  ResolveClassificationInput,
  SupportedAIApp,
} from '../types';
import {
  AI_OPERATIONS,
  APP_DEFAULT_CLASSIFICATION,
  APP_DEFAULT_OPERATION,
  PROVENANCE_VERSION,
  getOperationDefinition,
} from './catalog';

/**
 * Resolve classification from a trusted operation ID.
 * Any client-supplied classification is ignored (anti-spoof).
 */
export function resolveClassification(input: ResolveClassificationInput): AIContentClassification {
  const def = getOperationDefinition(input.operation);
  if (def) {
    if (def.app !== input.app) {
      // Operation belongs to another app — fall back to requesting app default
      // rather than trusting a mismatched client payload.
      return APP_DEFAULT_CLASSIFICATION[input.app];
    }
    return def.classification;
  }
  return APP_DEFAULT_CLASSIFICATION[input.app];
}

export function classificationToFlags(classification: AIContentClassification): {
  ai_generated: boolean;
  ai_modified: boolean;
  ai_assisted: boolean;
} {
  return {
    ai_generated: classification === 'AI_GENERATED',
    ai_modified: classification === 'AI_MODIFIED',
    ai_assisted: classification === 'AI_ASSISTED',
  };
}

export function computeProvenanceHealth(opts: {
  classification: AIContentClassification;
  operation: string;
  visibleDisclosureApplied: boolean;
  machineReadableApplied: boolean;
  provenanceFailed?: boolean;
  hasSourceAsset?: boolean;
  transparencyEnabled?: boolean;
  provenanceEnabled?: boolean;
}): ProvenanceHealth {
  const def = getOperationDefinition(opts.operation);
  const requireMR = def?.requireMachineReadable ?? opts.classification !== 'AI_ASSISTED';

  if (opts.transparencyEnabled === false) {
    return 'NOT_REQUIRED';
  }

  if (!requireMR || opts.provenanceEnabled === false) {
    if (opts.classification === 'AI_ASSISTED') return 'NOT_REQUIRED';
    // Visible disclosure still expected for MODIFIED/GENERATED
    if (opts.visibleDisclosureApplied) return 'PROVENANCE_PARTIAL';
    return 'PROVENANCE_PARTIAL';
  }

  if (opts.provenanceFailed) return 'PROVENANCE_FAILED';

  const hasVisible = opts.visibleDisclosureApplied;
  const hasMR = opts.machineReadableApplied;
  const hasSource = opts.hasSourceAsset !== false;

  if (hasVisible && hasMR && hasSource) return 'PROVENANCE_COMPLETE';
  if (hasVisible || hasMR) return 'PROVENANCE_PARTIAL';
  return 'PROVENANCE_FAILED';
}

export type BuildProvenanceInput = {
  app: SupportedAIApp;
  operation: string;
  sourceAssetId?: string;
  outputAssetId?: string;
  provider?: string;
  model?: string;
  createdAt?: string;
  visibleDisclosure?: boolean;
  machineReadable?: boolean;
  machineReadableApplied?: boolean;
  provenanceFailed?: boolean;
  provenanceFailureReason?: string | null;
  transparencyEnabled?: boolean;
  provenanceEnabled?: boolean;
  /** Rejected if present — classification always resolved from operation. */
  clientClassification?: unknown;
};

export function buildProvenanceRecord(input: BuildProvenanceInput): AIProvenanceRecord {
  const operation = input.operation || APP_DEFAULT_OPERATION[input.app];
  const def = getOperationDefinition(operation);
  const classification = resolveClassification({
    app: input.app,
    operation,
    clientClassification: input.clientClassification,
  });

  const visible =
    input.visibleDisclosure ??
    (def?.visibleDisclosure !== 'none' && (input.transparencyEnabled ?? true));

  const machineReadable =
    input.machineReadable ??
    Boolean(def?.requireMachineReadable ?? classification !== 'AI_ASSISTED');

  return {
    classification,
    provider: input.provider ?? def?.provider,
    model: input.model ?? def?.model,
    operation,
    app: input.app,
    sourceAssetId: input.sourceAssetId,
    outputAssetId: input.outputAssetId,
    createdAt: input.createdAt ?? new Date().toISOString(),
    provenanceVersion: PROVENANCE_VERSION,
    disclosure: {
      visible,
      machineReadable,
    },
  };
}

export function buildProvenanceFlags(
  record: AIProvenanceRecord,
  extras?: {
    machineReadableApplied?: boolean;
    provenanceFailed?: boolean;
    provenanceFailureReason?: string | null;
    transparencyEnabled?: boolean;
    provenanceEnabled?: boolean;
  },
): AIProvenanceFlags {
  const flags = classificationToFlags(record.classification);
  const machineReadableApplied = extras?.machineReadableApplied ?? false;
  const health = computeProvenanceHealth({
    classification: record.classification,
    operation: record.operation,
    visibleDisclosureApplied: record.disclosure.visible,
    machineReadableApplied,
    provenanceFailed: extras?.provenanceFailed,
    hasSourceAsset: Boolean(record.sourceAssetId),
    transparencyEnabled: extras?.transparencyEnabled,
    provenanceEnabled: extras?.provenanceEnabled,
  });

  return {
    ...flags,
    ai_classification: record.classification,
    ai_provider: record.provider ?? null,
    ai_model: record.model ?? null,
    ai_operation: record.operation,
    source_asset_id: record.sourceAssetId ?? null,
    output_asset_id: record.outputAssetId ?? null,
    ai_created_at: record.createdAt,
    provenance_version: record.provenanceVersion,
    visible_disclosure_applied: record.disclosure.visible,
    machine_readable_provenance_applied: machineReadableApplied,
    provenance_health: health,
    provenance_failure_reason: extras?.provenanceFailureReason ?? null,
  };
}

/** Safe metadata blob for generations.metadata / job JSON — no secrets/prompts. */
export function toSafeMetadata(record: AIProvenanceRecord, flags: AIProvenanceFlags): Record<string, unknown> {
  return {
    ai_transparency: {
      classification: record.classification,
      operation: record.operation,
      app: record.app,
      provider: record.provider ?? null,
      model: record.model ?? null,
      sourceAssetId: record.sourceAssetId ?? null,
      outputAssetId: record.outputAssetId ?? null,
      createdAt: record.createdAt,
      provenanceVersion: record.provenanceVersion,
      disclosure: record.disclosure,
      provenanceHealth: flags.provenance_health,
      machineReadableApplied: flags.machine_readable_provenance_applied,
    },
  };
}

export function isSupportedApp(value: string): value is SupportedAIApp {
  return value === 'photoclear' || value === 'hyperdusk' || value === 'staging' || value === 'imagemotion';
}

export function isKnownOperation(operation: string): boolean {
  return operation in AI_OPERATIONS;
}
